import Layout from "../components/Layout/Layout";
import Articlebanner from "../components/Articlebanner/Articlebanner";
import ArticlebannerLoading from "../components/Articlebanner/ArticlebannerLoading";
import Author from "../components/Author/Author";
import NotFound from "./NotFound";
import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { usePageMeta } from "../hooks/usePageMeta";

function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function resolveUrl(backend, path) {
  if (!path) return null;
  return path.startsWith("http") ? path : `${backend.replace(/\/$/, "")}${path}`;
}

function ArticleDetail() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const { id } = useParams();
  const apiPath = process.env.REACT_APP_PAGES_API_PATH;
  const apiKey = process.env.REACT_APP_API_KEY;
  const backend = process.env.REACT_APP_BACKEND_LINK;

  useEffect(() => {
    setLoading(true);
    setData(null);

    const getData = async () => {
      try {
        const response = await axios.get(apiPath + `${id}/`, {
          headers: { Authorization: `Api-Key ${apiKey}` },
        });
        setData(response.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setNotFound(true);
        }
      } finally {
        setLoading(false);
      }
    };

    getData();
  }, [id, apiPath, apiKey]);

  const publishedDate = formatDate(data?.meta?.first_published_at);
  const editedDate = formatDate(data?.meta?.last_published_at);

  const metaTitle = data?.meta?.seo_title || (data?.title ? `${data.title} | Vasapon Portfolio` : null);
  const metaDescription = data?.meta?.search_description || data?.intro || null;
  const authorName = data?.author_name || null;
  const ogImage =
    resolveUrl(backend, data?.thumbnail?.url) ||
    resolveUrl(backend, data?.cover?.meta?.download_url);
  const pageUrl = window.location.href;

  usePageMeta({
    title: metaTitle,
    description: metaDescription,
    ogType: "article",
    ogImage: ogImage,
    ogUrl: pageUrl,
    authorName: authorName,
  });

  if (notFound) return <NotFound />;

  return (
    <Layout>

      {loading ? (
        <ArticlebannerLoading />
      ) : (
        <>
          <Articlebanner image={data?.cover ? data.cover.meta.download_url : ""} />

          <section id="article-meta">
            <div className="article-meta-inner">
              <div className="article-title-block">
                <h1 className="article-title-text">{data?.title}</h1>
                {data?.intro && (
                  <p className="article-intro-text">{data.intro}</p>
                )}
              </div>
              {(publishedDate || editedDate) && (
                <div className="article-dates">
                  {publishedDate && (
                    <p className="article-date-line">
                      <strong>Published:</strong> {publishedDate}
                    </p>
                  )}
                  {editedDate && editedDate !== publishedDate && (
                    <p className="article-date-line">
                      <strong>Edited:</strong> {editedDate}
                    </p>
                  )}
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {loading ? (
        <section id="article-body">
          <div className="article-body-container">
            {[100, 85, 92, 60].map((w, i) => (
              <div key={i} className="shimmer" style={{ height: 18, borderRadius: 6, marginBottom: 12, width: `${w}%` }} />
            ))}
            <div className="shimmer" style={{ height: 320, borderRadius: 8, margin: "16px 0" }} />
            {[100, 78, 88, 50].map((w, i) => (
              <div key={i} className="shimmer" style={{ height: 18, borderRadius: 6, marginBottom: 12, width: `${w}%` }} />
            ))}
          </div>
        </section>
      ) : (
        <section id="article-body">
          {data?.body ? (
            <div
              className="article-body-container"
              dangerouslySetInnerHTML={{
                __html: data.body.replace(
                  /src="(\/[^"]+)"/g,
                  `src="${backend.replace(/\/$/, "")}$1"`,
                ),
              }}
            />
          ) : (
            <div className="no-data">
              <p className="body-large" style={{ fontWeight: "bold" }}>
                No Data Found
              </p>
            </div>
          )}
        </section>
      )}

      {!loading && data?.author?.id && (
        <Author author_id={data.author.id} />
      )}
    </Layout>
  );
}

export default ArticleDetail;
